/**
 * GRIDHEIMR Web Dashboard Main Application
 *
 * Handles routing and authentication flow.
 */

import React from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { Login } from "./components/Login";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { DashboardPage } from "./pages/DashboardPage";
import { CreateBotPage } from "./pages/CreateBotPage";
import { BotDetailPage } from "./pages/BotDetailPage";
import { CredentialsPage } from "./pages/CredentialsPage";
import { HatcheryLabPage } from "./pages/HatcheryLabPage";
import { LandingPage } from "./pages/LandingPage";
import { RegisterPage } from "./pages/RegisterPage";
import { BLUE, YELLOW, PIXEL, MONO } from "./theme";

/**
 * Protected Route Component
 *
 * Redirects to login if user is not authenticated.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingText}>Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

/**
 * Public Route Component
 *
 * Redirects to dashboard if user is already authenticated.
 */
interface PublicRouteProps {
  children: React.ReactNode;
}

function PublicRoute({ children }: PublicRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingText}>Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <>{children}</>;
}

/**
 * Main Application Component
 */
function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* /bots — redirect to dashboard (bot list is on dashboard) */}
      <Route
        path="/bots"
        element={<Navigate to="/dashboard" replace />}
      />
      <Route
        path="/bots/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateBotPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bots/:botId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <BotDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/credentials"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CredentialsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lab"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <HatcheryLabPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* 404 Not Found */}
      <Route
        path="*"
        element={
          <div style={styles.notFoundContainer}>
            <h1 style={styles.notFoundTitle}>
              <span style={{ color: BLUE }}>4</span>
              <span style={{ color: YELLOW }}>0</span>
              <span style={{ color: BLUE }}>4</span>
            </h1>
            <p style={styles.notFoundText}>// page not found</p>
            <Link to="/dashboard" style={styles.notFoundLink}>
              ← return to grid
            </Link>
          </div>
        }
      />
    </Routes>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#000a12",
  },
  loadingText: {
    fontFamily: PIXEL,
    fontSize: "0.6rem",
    color: BLUE,
    letterSpacing: "0.15em",
    textShadow: `0 0 12px rgba(0,106,167,0.5)`,
  },
  notFoundContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#000a12",
    padding: "2rem",
    gap: "1.5rem",
  },
  notFoundTitle: {
    fontFamily: PIXEL,
    fontSize: "clamp(3rem, 12vw, 6rem)",
    margin: 0,
    letterSpacing: "0.1em",
    textShadow: `0 0 30px rgba(0,106,167,0.4)`,
  },
  notFoundText: {
    fontFamily: MONO,
    fontSize: "1rem",
    margin: 0,
    color: "rgba(255,255,255,0.4)",
    letterSpacing: "0.1em",
  },
  notFoundLink: {
    fontFamily: PIXEL,
    fontSize: "0.45rem",
    color: YELLOW,
    textDecoration: "none",
    padding: "12px 24px",
    border: `2px solid ${YELLOW}`,
    letterSpacing: "0.1em",
    transition: "all 0.2s ease",
    boxShadow: `0 0 10px rgba(254,204,0,0.2)`,
  },
};

export default App;
